import {cloneElement, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";

import {useTable} from "base/table/hooks";
import {useList} from "base/entity/provider";

import {ErrorAlerts} from "base/error/components";
import {TableCustomCell, TableCustomHead} from "base/table/components";
import {AuthAction} from "base/user/components";
import {ActionsMenu} from "base/ui/menu";
import {Spinner} from "base/shared/components";
import {EntityNoItemsComponent} from "base/entity/components/presentational";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {EntityTableDirectDownloadButton} from "permit/container/EntityTableDirectDownloadButton";

const pageSize = parseInt(process.env.REACT_APP_PAGE_SIZE);

const tableRowStyle = {
    cursor: "pointer",
};

const EntityTable = ({
    columns,
    service,
    defaultFilter = null,
    selectedElement = null,
    onSelectElement = null,
    elementActions = [],
    getCellProps = null,
    isFilterEmpty,
    enableExport = false,
}) => {
    const location = useLocation();
    const {
        filter,
        page,
        setPage,
        size,
        setSize,
        sort,
        setSort,
        order,
        setOrder,
        isLoading,
        setIsLoading,
    } = useList();

    const [elements, setElements] = useState([]);
    const [error, setError] = useState(null);

    const {headCells, getValue, getCellStyle} = useTable(
        elements,
        columns,
        elementActions?.length
    );

    const noElements = !size && !elements?.length;

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        const serviceCall =
            typeof service === "function"
                ? service({...defaultFilter, ...filter}, page, sort, order)
                : service.getPaginatedList(
                      {...defaultFilter, ...filter},
                      page,
                      sort,
                      order
                  );
        serviceCall
            .then(data => {
                setElements(data.results);
                setSize(data.count);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
                setError(error);
            });
    }, [filter, page, sort, order, location.state?.lastRefreshDate]);

    const handleSelectElement = (event, element) => {
        const cellIndex = event.target.cellIndex;
        if (
            cellIndex !== undefined &&
            cellIndex !== columns.length &&
            onSelectElement
        ) {
            onSelectElement(element.id);
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sort === property && order === "asc";
        setSort(property);
        setOrder(isAsc ? "desc" : "asc");
        setSort(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // const getNestedAttributeValue = (element, attribute) => {
    //     let returnData = element;

    //     attribute.split(".").forEach(subPath => {
    //         returnData = returnData[subPath] || "";
    //     });

    //     return returnData;
    // };

    // const getAttributeValue = (element, attribute) => {
    //     if (attribute.indexOf(".") >= 0) {
    //         return getNestedAttributeValue(element, attribute);
    //     }
    //     return element[attribute];
    // };

    // const headCells = elementActions
    //     ? [
    //           ...columns,
    //           {
    //               id: "actions",
    //               width: 7,
    //           },
    //       ]
    //     : columns;

    const getValueNote = (cellAttribute, element) => {
        return cellAttribute.note ? cellAttribute.note(element) : null;
    };

    return (
        <>
            <ErrorAlerts error={error} />
            {isLoading ? (
                <Spinner />
            ) : noElements ? (
                <EntityNoItemsComponent isFilterEmpty={isFilterEmpty} />
            ) : (
                elements && (
                    <TableContainer sx={{width: "100%"}}>
                        <Table
                            aria-labelledby="Tabla"
                            size="small"
                            sx={{tableLayout: "fixed"}}
                        >
                            <TableCustomHead
                                order={order}
                                attribute={sort}
                                onRequestSort={handleRequestSort}
                                headCells={headCells}
                                showActions={elementActions?.length > 0}
                            />
                            <TableBody>
                                {elements?.map((element, index) => {
                                    return (
                                        <TableRow
                                            hover
                                            key={index}
                                            sx={tableRowStyle}
                                            selected={selectedElement === element.id}
                                            onClick={event =>
                                                handleSelectElement(event, element)
                                            }
                                        >
                                            {columns.map((cellAttribute, index) => {
                                                const valueNote = getValueNote(
                                                    cellAttribute,
                                                    element
                                                );
                                                return (
                                                    <TableCustomCell
                                                        key={index}
                                                        id={cellAttribute.id}
                                                        TableCellProps={
                                                            getCellProps
                                                                ? getCellProps(element)
                                                                : null
                                                        }
                                                        style={getCellStyle(
                                                            cellAttribute,
                                                            element
                                                        )}
                                                    >
                                                        {cellAttribute.formatFunction
                                                            ? cellAttribute.formatFunction(
                                                                  element
                                                              )
                                                            : getValue(
                                                                  element,
                                                                  cellAttribute.id
                                                              )}
                                                        {valueNote ? (
                                                            <Tooltip title={valueNote}>
                                                                <Typography component="span">
                                                                    {" "}
                                                                    *
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : null}
                                                    </TableCustomCell>
                                                );
                                            })}
                                            {elementActions?.length ? (
                                                <TableCell>
                                                    <AuthAction roles={[]}>
                                                        <ActionsMenu>
                                                            {elementActions.map(
                                                                actionMenu =>
                                                                    cloneElement(
                                                                        actionMenu,
                                                                        {
                                                                            element,
                                                                        }
                                                                    )
                                                            )}
                                                        </ActionsMenu>
                                                    </AuthAction>
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {page && (
                            // flex-end
                            <Grid container justifyContent="space-between" sx={{mt: 3}}>
                                {enableExport && (
                                    <Grid item>
                                        <EntityTableDirectDownloadButton
                                            service={service}
                                            filter={filter}
                                        />
                                    </Grid>
                                )}
                                <Grid item>
                                    <Pagination
                                        count={Math.ceil(size / pageSize)}
                                        page={page}
                                        onChange={handleChangePage}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </TableContainer>
                )
            )}
        </>
    );
};

export default EntityTable;
